import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import useBrands from 'pared/layouts/hooks/useBrands'
import { getBrandLocationGroupId } from 'pared/utils/brand'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'

const query = gql`
  query systemwideOverviewKpis(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
    $iAverageWeeklySalesFilter: JSON!
    $iInputParams: JSON!
    $useCustom: Boolean!
    $useUnified: Boolean!
  ) {
    summaryLocationGroupFinancialKpis(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        netSales
        netSalesLy: yoyNetSales
        netSalesBudgetVariance
        netSalesSssPercent
        grossSales
        checkCount
        checkCountLy: yoyTotalCheckCount
        checkAverage
        checkAverageLy: yoyCheckAverage
        hourlyLaborCostPercent
        hourlyLaborBudgetVariance
        cogsPercent
        cogsBudgetVariance
        avtPercent
        rcpPercent
        expoMSssPercent
      }
    }

    getCustomizedReport(
      iCustomizedReportName: "LIST_LOCATION_GROUP_FINANCIAL_KPIS"
      iInputParams: $iInputParams
    ) @include(if: $useCustom) {
      nodes {
        reportName
        reportDefinition
        reportResult
      }
    }

    listLocationGroupAverageWeeklySales(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iAverageWeeklySalesFilter
    ) @include(if: $useUnified) {
      nodes {
        metricSummaryData
      }
    }
  }
`

export const systemwideOverviewKpisConfigs = {
  netSales: 'price',
  netSalesLy: 'price',
  netSalesBudgetVariance: 'percent',
  netSalesSssPercent: 'percent',
  grossSales: 'price',
  checkCount: 'number',
  checkCountLy: 'number',
  checkCountVsLyPercent: 'percent',
  checkAverage: 'price',
  checkAverageLy: 'price',
  checkAverageLyPercent: 'percent',
  hourlyLaborCostPercent: 'percent',
  hourlyLaborBudgetVariance: 'percent',
  cogsPercent: 'percent',
  cogsBudgetVariance: 'percent',
  avtPercent: 'percent',
  rcpPercent: 'percent',
  expoMSssPercent: 'percent',

  // customized bibibop
  fAndPPercent: 'percent',
  laborPercent: 'percent',
  fplPercent: 'percent',
  ebitdaBeforeBonusPercent: 'percent',
  ebitdaAfterBonusPercent: 'percent',
  grossProfitPercent: 'percent',
  restaurantLevelProfitPercent: 'percent',

  // customized mwb
  foodCost: 'percent',
  paperCost: 'percent',
  variableLaborCost: 'percent',
  primeCost: 'percent',
  actualFbPercent: 'percent',
  theoreticalFbPercent: 'percent',
  avtFbPercent: 'percent',

  // unified endpoint
  averageWeeklySales: 'price',
} as const

const USE_CUSTOM = ['bibibop', 'mwb']
const USE_UNIFIED = ['mwb']

const useSystemwideOverviewKpis = () => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()
  const { brand } = useBrands()
  const { data, loading } = useQuery(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter: { location_group_ids: groupFilter?.ids },
      iAverageWeeklySalesFilter: {
        // For MWB, always show company average weekly sales
        location_group_ids: [getBrandLocationGroupId()],
        metrics: ['net_sales'],
      },
      iInputParams: {
        startDate: startDate,
        endDate: endDate,
        locationGroupIds: groupFilter?.ids,
      },
      useCustom: USE_CUSTOM.includes(brand),
      useUnified: USE_UNIFIED.includes(brand),
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo(() => {
      const kpis = {
        ...(data?.summaryLocationGroupFinancialKpis.nodes[0] || {}),
      }
      const netSalesLy = parseInt(kpis.netSalesLy, 10)
      const checkCount = parseInt(kpis.checkCount, 10)
      const checkCountLy = parseInt(kpis.checkCountLy, 10)
      const customizedData = data?.getCustomizedReport?.nodes[0]?.reportResult
        ?.tableData?.[0] as Record<string, unknown> | undefined

      if (checkCount && checkCountLy)
        kpis.checkCountVsLyPercent =
          (100.0 * (checkCount - checkCountLy)) / checkCountLy

      if (netSalesLy && checkCountLy) {
        if (kpis.checkAverage) {
          kpis.checkAverageLyPercent =
            (100.0 * (kpis.checkAverage - kpis.checkAverageLy)) /
            kpis.checkAverage
        }
      }

      const averageWeeklySales =
        data?.listLocationGroupAverageWeeklySales?.nodes[0]?.metricSummaryData
          ?.averageWeeklySales ?? {}
      if (Object.keys(kpis).length === 0) return null

      return {
        ...kpis,
        ...customizedData,
        averageWeeklySales:
          averageWeeklySales.unit === 'CENT'
            ? averageWeeklySales.value
            : averageWeeklySales.value * 100,
      }
    }, [data]),
    loading,
  }
}

export default useSystemwideOverviewKpis
